import { IRichCellEditorParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { AxiosResponse } from "axios";
import { MouseEventHandler, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { LuExternalLink } from "react-icons/lu";
import { RxCrossCircled } from "react-icons/rx";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { ColumnsToolPanelModule, FiltersToolPanelModule, SetFilterModule } from "ag-grid-enterprise";
import useFetch from "../../../../hooks/useFetchMSAL";
import useInput from "../../../../hooks/useInput";
import { TradeAndPromotionPlanningFetchAPI } from "../../../../models/APIResponses.model";
import { PromotionDropdown } from "../../../../models/TradeAndPromoPlanning/PromotionPlanningGridDropdown.model";
import VolumeForecast from "../../../../models/TradeAndPromoPlanning/VolumeForecast.model";
import { VolumeForecastDropdownModel, VolumeForecastSelectedSlicerModel } from "../../../../models/TradeAndPromoPlanning/VolumeForecastSlicer.model";
import { AG_GRID_DEFAULT_COL_DEF, AG_GRID_MODULES, AG_GRID_OPTIONS,
        ANNUAL_SUMMARY_TAB_ID, FORECAST_REVIEW_TAB_ID, PROMOTION_PLANNING_TAB_ID,
        SHORT_TERM_PROMOTION_SUB_TAB_ID, TOAST_CONTAINER_ID, TRADE_SPEND_REPORT_TAB_ID,
        VOLUME_FORECAST_TAB_ID } from "../../../../shared/constants";
import { createDescription, getReOrderedAPIData, getToastOptions, usaNumberFormatter } from "../../../../shared/functions";
import { RootState } from "../../../../store";
import { tradeAndPromoPlanningTabActions } from "../../../../store/slices/trade-and-promo-planning-tab-slice";
import { volumeForecastTabActions } from "../../../../store/slices/volume-forecast-tab-slice";
import EditableGridActions from "../../../UI/EditableGridActions/EditableGridActions";
import Modal from "../../../UI/Modal/Modal";
import VolumeForecastSlicers from "./VolumeForecastSlicers";
import HierarchySlicers from "./HierarchySlicers";
import ChangeLogTable from "../../../UI/ChangeLog/ChangeLogTable";

const rowLabelsAndIndexes: {
    [key: string]: {
        label: string;
        rowIndex: number;
        editable: boolean;
    }
} = {
    events_in_week: {
        label: "Events in Week",
        rowIndex: 0,
        editable: false
    },
    baseline_forecast: {
        label: "Baseline Forecast",
        rowIndex: 1,
        editable: true
    },
    stat_forecast: {
        label: "Stat Forecast",
        rowIndex: 2,
        editable: false
    },
    volume_adjustment_1: {
        label: "Volume Adjustment 1",
        rowIndex: 3,
        editable: true
    },
    volume_adjustment_2: {
        label: "Volume Adjustment 2",
        rowIndex: 4,
        editable: true
    },
    final_adjusted_baseline_forecast: {
        label: "Final Adjusted Baseline Forecast",
        rowIndex: 5,
        editable: false
    },
    promotion: {
        label: "Promotion",
        rowIndex: 6,
        editable: true
    },
    promotional_lift: {
        label: "Promotional Lift",
        rowIndex: 7,
        editable: false
    },
    // final_nap: {
    //     label: "Final Promotional Price ($)",
    //     rowIndex: 8,
    //     editable: false
    // },
    override: {
        label: "Override",
        rowIndex: 8,
        editable: true
    },
    final_consumption_forecast: {
        label: "Final Consumption Forecast",
        rowIndex: 9,
        editable: false
    },
    take_rate: {
        label: "Take Rate (%)",
        rowIndex: 10,
        editable: false
    },
    scan_volume: {
        label: "Scan Volume",
        rowIndex: 11,
        editable: false
    },
    initial_shipment_forecast: {
        label: "Initial Shipment Forecast",
        rowIndex: 12,
        editable: false
    },
    shipment_adjustment_1: {
        label: "Shipment Adjustment 1",
        rowIndex: 13,
        editable: true
    },
    final_shipment_forecast: {
        label: "Updated Shipment Forecast",
        rowIndex: 14,
        editable: false
    },
    percentage_source_from_wholesaler: {
        label: "Percentage Sourced From Wholesaler",
        rowIndex: 15,
        editable: true
    },
    direct_shipment_forecast: {
        label: "Direct Shipment Forecast",
        rowIndex: 16,
        editable: false
    },
    promotion_calendar_id: {
        label: "Promotion Calendar ID",
        rowIndex: 17,
        editable: false
    },
    promotion_type: {
        label: "Promotion Type",
        rowIndex: 18,
        editable: false
    }
};

const VolumeForecastGrid = (props: {
    gridHeight: string;
}) => {
    // States
    const [gridRowData, setGridRowData] = useState<VolumeForecast[]>([]);
    const [gridRowDataCopy, setGridRowDataCopy] = useState<VolumeForecast[]>([]);
    const [gridColDef, setGridColDef] = useState<any[]>([]);
    const [promotionDropdownData, setPromotionDropdownData] = useState<PromotionDropdown[]>([]);
    const [editedColIds, setEditedColIds] = useState<any[]>([]);
    const [gridCellEdits, setGridCellEdits] = useState<{
        [key: string]: {
            [key: string]: string | number;
        };
    }>({});
    const [changeLogData, setChangeLogData] = useState([]);
    const [changeLogDataFetched, setChangeLogDataFetched] = useState(false);

    // Variables
    const agGridRef = useRef<AgGridReact>(null);
    const [, fetchData] = useFetch([]);
    const modalCloseBtnRef = useRef<HTMLButtonElement>(null);
    const lagUpdateModalID: string = "lagUpdateModal";
    const {
        value: newLagValue,
        valueIsValid: newLagValueIsValid,
        valueHasError: newLagValueHasError,
        valueChangeHandler: newLagValueChangeHandler,
        valueBlurHandler: newLagValueBlurHandler,
        resetEnteredValue: resetNewLagValue
    } = useInput((value: string) => value && value.length > 0 && Number(value) >= -5 && Number(value) <= 0);

    // Redux variables
    const dispatchFn = useDispatch();
    const globalCustomer: { id: number; name: string; } = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.globalCustomer);
    const tabApiDataFetched: boolean = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.tabApiDataFetched)[VOLUME_FORECAST_TAB_ID];
    const activeSubTabId: string = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.activeSubTabId);
    const selectedQuarters = useSelector((state: RootState) => state.volumeForecastTab.selectedQuarters);
    const selectedPeriods = useSelector((state: RootState) => state.volumeForecastTab.selectedPeriods);
    const selectedHierarchies = useSelector((state: RootState) => state.volumeForecastTab.selectedHierarchies);
    const selectedSlicers: VolumeForecastSelectedSlicerModel = useSelector((state: RootState) => state.volumeForecastTab.selectedSlicers);

    const [finalConsumptionForecast, setFinalConsumptionForecast] = useState<any>([]);

    /**
     * Handler to close consent modal
     */
    const closeLagEditModal = () => {
        modalCloseBtnRef.current?.click();
    };

    /**
     * Returns ColDef for Volume Forecast data grid
     * @param {ReplenishmentPlanSummary[]} apiData Data from GET /volume-forecast API
     * @param {PromotionDropdown[]} promotionDropdownData Promotion row dropdown data from GET /volume-forecast API
     * @returns {any[]} Generated ColDef for Volume Forecast data grid
     */
    const generateColDef = (apiData: VolumeForecast[], promotionDropdownData: PromotionDropdown[]): any[] => {
        const newColDef: any[] = [
            {
                field: "customer_name",
                tooltipField: "customer_name",
                headerTooltip: "Customer",
                cellDataType: "text",
                headerName: "Customer",
                pinned: "left",
                wrapText: true,
                autoHeight: true,
                width: 150,
                cellRenderer: (params: any) => {
                    if (params.data.variable === "initial_shipment_forecast") {
                        return (
                            <div className="row">
                                <div className="col col-12">
                                    {params.value}
                                    <span className="me-2"></span>
                                    <LuExternalLink size={18} color={"#094780"} type="button" title="Update lag value" data-bs-toggle="modal" data-bs-target={`#${lagUpdateModalID}`} />
                                </div>
                            </div>
                        );
                    }

                    return params.value;
                }
            },
        ];

        if (selectedHierarchies.product === "Brand-Sub Brand-Volume Size") {
            newColDef.push(
                {
                    field: "sku_name",
                    tooltipField: "sku_name",
                    headerTooltip: "Product",
                    cellDataType: "text",
                    headerName: "Product",
                    pinned: "left",
                    wrapText: true,
                    autoHeight: true,
                    width: 250
                }
            )
        }else if(selectedHierarchies.product === "Brand-Sub Brand"){
            newColDef.push(
                {
                    field: "brand",
                    tooltipField: "brand",
                    headerTooltip: "Brand",
                    cellDataType: "text",
                    headerName: "Brand",
                    pinned: "left",
                    wrapText: true,
                    autoHeight: true,
                    width: 150
                }
            );
            newColDef.push(
                {
                    field: "sub_brand",
                    tooltipField: "sub_brand",
                    headerTooltip: "Sub Brand",
                    cellDataType: "text",
                    headerName: "Sub Brand",
                    pinned: "left",
                    wrapText: true,
                    autoHeight: true,
                    width: 250
                }
            )
        }else{
            newColDef.push(
                {
                    field: "brand",
                    tooltipField: "brand",
                    headerTooltip: "Brand",
                    cellDataType: "text",
                    headerName: "Brand",
                    pinned: "left",
                    wrapText: true,
                    autoHeight: true,
                    width: 150
                }
            )
        }

        newColDef.push(
            {
                field: "variable",
                headerTooltip: "Measures",
                cellDataType: "text",
                headerName: "Measures",
                pinned: "left",
                wrapText: true,
                autoHeight: true,
                width: 170,
                menuTabs: ["filterMenuTab"],
                filter: "agSetColumnFilter",
                filterParams: {
                    refreshValuesOnOpen: true,
                    valueFormatter: (params: any) => rowLabelsAndIndexes[params.value].label
                },
                valueFormatter: (params: any): any => rowLabelsAndIndexes[params.data.variable].label,
                tooltipValueGetter: (params: any) => params.valueFormatted
            }
        )

        newColDef.push(
            {
                field: "total",
                headerTooltip: "Totals",
                cellDataType: "text",
                headerName: "Totals",
                cellClass: "ag-right-aligned-cell",
                pinned: "left",
                editable: (params: any) => (Object.keys(rowLabelsAndIndexes).filter(obj => (obj !== 'promotion') && (obj !== 'percentage_source_from_wholesaler')).filter(c => rowLabelsAndIndexes[c].editable).includes(params.data.variable)),
                width: 150,
                // valueSetter: (p:any) => {
                //     p.data['total'] =  usaNumberFormatter(parseInt(p.newValue), 0);
                //     return true;
                // },
                valueFormatter: (params: any): any => {
                    return (typeof params.data?.['total'] === "number") ?
                        usaNumberFormatter(params.data?.['total'], 0) :
                        params.data?.['total']
                },

            }
        )

        if (apiData && (apiData.length > 0)) {
            
            var sortedStrList: any;
            if(selectedHierarchies.timeline === 'Weekly'){
                const filteredDateStrList: string[] = Object.keys(apiData[0]).filter(key => Boolean(Date.parse(key)));
                
                sortedStrList = filteredDateStrList.sort((dateStr1, dateStr2) => {
                    const splitDate1 = dateStr1.split("/");
                    const splitDate2 = dateStr2.split("/");
    
                    const isoDateStr1 = `${splitDate1[2]}${splitDate1[0]}${splitDate1[1]}`;
                    const isoDateStr2 = `${splitDate2[2]}${splitDate2[0]}${splitDate2[1]}`;
    
                    return isoDateStr1.localeCompare(isoDateStr2);
                });
            }else if (selectedHierarchies.timeline === 'Quarterly') {
                const filteredDateStrList: string[] = Object.keys(apiData[0]).filter(key => /\d/.test(key));
                sortedStrList = filteredDateStrList.sort();
            }else if(selectedHierarchies.timeline === 'Periodic'){
                const filteredDateStrList: string[] = Object.keys(apiData[0]).filter(key =>  /\d/.test(key));
                sortedStrList = filteredDateStrList.sort((p1, p2)=>{
                    const period1 = parseInt(p1.split('-')[0].substring(1));
                    const period2 = parseInt(p2.split('-')[0].substring(1));
                    return period1-period2;
                });
            }
            
            newColDef.push(...sortedStrList.map((key:any) => {
                return {
                    field: key,
                    cellDataType: "text",
                    headerName: key,
                    headerTooltip: key,
                    wrapText: true,
                    autoHeight: (p:any)=>{
                        const rowLabel: string = rowLabelsAndIndexes[p.node.data.variable].label;
                        if(rowLabel === "Events in Week"){
                            return true
                        }
                    },
                    cellClass: "ag-right-aligned-cell",
                    headerClass: "ag-left-aligned-cell",
                    cellEditorSelector: (params: any) => {
                        const rowLabel: string = rowLabelsAndIndexes[params.node.data.variable].label;

                        return {
                            component: (rowLabel === "Promotion") ? "agRichSelectCellEditor" : "agTextCellEditor",
                            ...(
                                (rowLabel === "Promotion") ? {
                                    params: {
                                        values: promotionDropdownData.map(p => p.promotion_name),
                                        valueListMaxWidth: "250px",
                                        searchType: "match",
                                        allowTyping: true,
                                        filterList: true,
                                        highlightMatch: true
                                    } as IRichCellEditorParams
                                } : {}
                            )
                        }
                    },
                    editable: (params: any) => (Object.keys(rowLabelsAndIndexes).filter(c => rowLabelsAndIndexes[c].editable).includes(params.data.variable)),
                    valueSetter: (params: any) => {
                        const columnId: string = params.column.colId;

                        if (params.newValue) {
                            const rowLabel: string = rowLabelsAndIndexes[params.node.data.variable].label;

                            if (
                                rowLabel === "Baseline Forecast" || 
                                rowLabel === "Volume Adjustment 1" ||
                                rowLabel === "Volume Adjustment 2" ||
                                rowLabel === "Override" ||
                                rowLabel === "Shipment Adjustment 1"
                            ) {
                                params.data[columnId] = params.newValue === null ? null : usaNumberFormatter(parseFloat(params.newValue), 2);

                            } else if (rowLabel === "Percentage Sourced From Wholesaler") {
                                const newValue: number | null = parseFloat(params.newValue);
                                if (isNaN(newValue) || newValue < 0 || newValue > 100) {
                                    toast.error("Percentage Source From Wholesaler should be between 0 to 100", {
                                        ...getToastOptions("error"),
                                        containerId: TOAST_CONTAINER_ID
                                    });
                                    params.data[columnId] = params.oldValue;
                                } else {
                                    params.data[columnId] = usaNumberFormatter(newValue, 2);
                                }
                            } else {
                                params.data[columnId] = params.newValue;
                            }

                        } else {
                            params.data[params.column.colId] = null;
                        }
                        return true;
                    },
                    valueFormatter: (params: any): any => {
                        const rowLabel: string = rowLabelsAndIndexes[params.node?.data?.variable]?.label;

                        if (typeof params.value === "number") {
                            if (rowLabel === "Take Rate (%)") {
                                const numStr: string | null = params.value ? usaNumberFormatter(params.value, 2) : null;
                                return numStr ? `${numStr}%` : null;
                            } else if (rowLabel === "Final Promotional Price ($)") {
                                const numStr: string | null = usaNumberFormatter(params.value, 2);
                                return numStr ? `$${numStr}` : numStr;
                            } else {
                                return params.value === null ? null : usaNumberFormatter(params.value, 0);
                            }
                        } else {
                            return params.value;
                        }
                    },
                    tooltipValueGetter: (params: any) => params.valueFormatted
                    
                };
            }));
        }

        return newColDef;
    };

    /**
     * Re-orders API data according to the required AG Grid row order.
     * @param {VolumeForecast[]} fetchApiData API data array fetched from Volume Forecast data API
     * @returns {VolumeForecast[]} Re-ordered API data array
     */
    const reOrderGridRows = useCallback(
        (fetchApiData: VolumeForecast[]): VolumeForecast[] => getReOrderedAPIData(rowLabelsAndIndexes, fetchApiData),
        []
    );

    /**
     * Event handler for styling AG Grid rows (Promotion, Override)
     * @param params AG Grid Row params
     * @returns {any} CSS styles in JSON form
     */
    const gridRowStyleHandler = (params: any) => {
        const rowLabel: string = rowLabelsAndIndexes[params.data.variable]?.label;

        return {
            backgroundColor: (
                rowLabel === "Baseline Forecast" || 
                rowLabel === "Promotion" ||
                rowLabel === "Override" ||
                rowLabel === "Volume Adjustment 1" ||
                rowLabel === "Volume Adjustment 2" ||
                rowLabel === "Shipment Adjustment 1" ||
                rowLabel === "Percentage Sourced From Wholesaler"
            ) ? "#ffffcc" : "#ffffff"
        };
    };

    /**
     * Removes highlight of edited cells
     */
    const removeEditedCellHighlights = useCallback((): void => {
        editedColIds.forEach((colId) => {
            agGridRef.current!.api.getColumnDef(colId)!.cellStyle = undefined;
        });
    }, [editedColIds]);

    /**
     * Event handler for cell value change in AG Grid
     * @param {any} params Cell value change event params
     */
    const cellValueChangeHandler = (params: any): void => {
        if (params.oldValue !== params.newValue) {
            params.colDef.cellStyle = (p: any) => (
                (p.rowIndex.toString() === params.node.id) ? { backgroundColor: '#8af38a' } : {}
            );

            params.api.refreshCells({
                force: true,
                columns: [params.column.getId()],
                rowNodes: [params.node]
            });

            if (editedColIds.indexOf(params.column.getId()) === -1) {
                setEditedColIds((prev) => [...prev, params.column.getId()]);
            }
            setGridCellEdits((cellEdits) => {
                const rowField: string = params.data.variable;
                const colId: string = params.column.getId();
                const updatedNewValue: number | null = (params.newValue === null) ? params.newValue : (
                    (
                        rowField === "baseline_forecast" || 
                        rowField === "override" ||
                        rowField === "shipment_adjustment_1" ||
                        rowField === "volume_adjustment_1" ||
                        rowField === "volume_adjustment_2"
                    ) ? parseFloat(params.newValue.replaceAll(",", "")) : (rowField === "percentage_source_from_wholesaler") ? (params.newValue / 100) : params.newValue
                );

                if (cellEdits[rowField]) {
                    return {
                        ...cellEdits,
                        [rowField]: {
                            ...cellEdits[rowField],
                            [colId]: updatedNewValue
                        }
                    };
                }

                const updatedCellEdits: any = {
                    ...cellEdits,
                    [rowField]: {
                        [colId]: updatedNewValue
                    }
                };
                return updatedCellEdits;
            });
        }
    };

    /**
     * Update grid data and slicers from fetch / update API response.
     * @param apiResponse API Response.
     */
    const updateGridDataAndSlicers = (apiResponse: {
        result: {
            data: VolumeForecast[];
            dropdown_data: {
                promotions: PromotionDropdown[];
            }
        },
        slicers: VolumeForecastDropdownModel
    }): void => {
        const apiData: VolumeForecast[] = apiResponse.result.data;
        const reOrderedApiData: VolumeForecast[] = reOrderGridRows(apiData);
        const promoDropdownData: PromotionDropdown[] = apiResponse.result.dropdown_data.promotions;

        setGridCellEdits({});
        setGridRowData(structuredClone(reOrderedApiData));
        setGridRowDataCopy(structuredClone(reOrderedApiData));
        setPromotionDropdownData(structuredClone(promoDropdownData));
        setGridColDef((prevColDef) => generateColDef(reOrderedApiData, promoDropdownData));

        dispatchFn(volumeForecastTabActions.setSlicers(structuredClone(apiResponse.slicers)));
    };

    /**
     * Async function to fetch data from GET /volume-forecast
     */
    const fetchVolumeForecastData = useCallback(async (): Promise<void> => {
        const fetchingDataToastID = toast.loading('Fetching Volume Forecast data...', {
            containerId: TOAST_CONTAINER_ID,
            ...getToastOptions('loading')
        });

        try {
            const fetchAPIResponse: AxiosResponse<TradeAndPromotionPlanningFetchAPI<{
                result: {
                    data: VolumeForecast[];
                    dropdown_data: {
                        promotions: PromotionDropdown[];
                    }
                },
                slicers: VolumeForecastDropdownModel
            }>> = await fetchData("/volume-forecast", {
                method: "POST",
                params: {
                    customer_id: globalCustomer.id,
                    sku_id: selectedHierarchies.product === "Brand-Sub Brand-Volume Size" ? selectedSlicers.sku : 
                            selectedHierarchies.product === "Brand-Sub Brand" ? selectedSlicers.brandSubBrand : selectedSlicers.brand,
                    year: selectedSlicers.year,
                },
                data: {
                    quarters: selectedQuarters,
                    periods: selectedPeriods,
                    hierarchy: selectedHierarchies.product,
                    timeline: selectedHierarchies.timeline
                }
            });

            if (fetchAPIResponse.data.data) {
                setFinalConsumptionForecast(fetchAPIResponse.data.data.result.data.filter((r: any) => r.variable === 'final_consumption_forecast')[0])
                updateGridDataAndSlicers(fetchAPIResponse.data.data);
                dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
                    [VOLUME_FORECAST_TAB_ID]: true
                }));
                setChangeLogDataFetched(true);

            } else {
                toast.error('Error in fetching Volume Forecast data', {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions('error')
                });
            }
            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });

        } catch (error: Error | any) {
            console.error("Error ", error)
            console.error(`Request Error: ${error}`);
            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });
            toast.error((
                error?.response?.data?.error ||
                'Error in fetching Volume Forecast data'
            ), {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions('error')
            });
        }
    }, [globalCustomer.id, selectedSlicers, selectedQuarters, selectedPeriods, selectedHierarchies, dispatchFn, fetchData]);

    /**
       * Updates cell values by using Volume Forecast data update API
       * @param {any} updateReqBody Update API request body
       */
    const updateCellValues = useCallback(async (updateReqBody: any): Promise<void> => {
        try {
            const updateToastID = toast.loading("Updating Volume Forecast data...", {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions("loading"),
            });

            updateReqBody['description'] = createDescription(VOLUME_FORECAST_TAB_ID, 'update', {...gridCellEdits}, gridRowData);
            updateReqBody['tabname'] = VOLUME_FORECAST_TAB_ID;

            const hasTotalKey = Object.values(updateReqBody).some(value =>
                value && typeof value === 'object' && value.hasOwnProperty('total')
            );

            if (hasTotalKey) {
                let keysToRemove: any = ['customer_id', 'customer_name', 'sku_id', 'sku_name',
                     'variable', 'brand', 'sub_brand', 'rowIndex'];

                updateReqBody['final_consumption_forecast'] = Object.keys(finalConsumptionForecast)
                    .filter(key => !keysToRemove.includes(key))
                    .reduce((acc: any, key: any) => {
                        acc[key] = finalConsumptionForecast[key];
                        return acc;
                    }, {});
            }

            const updateAPIResponse: AxiosResponse<TradeAndPromotionPlanningFetchAPI<{
                result: {
                    data: VolumeForecast[];
                    dropdown_data: {
                        promotions: PromotionDropdown[];
                    }
                },
                slicers: VolumeForecastDropdownModel;
            }>> = await fetchData("/volume-forecast", {
                method: "PUT",
                params: {
                    customer_id: globalCustomer.id,
                    sku_id: selectedHierarchies.product === "Brand-Sub Brand-Volume Size" ? selectedSlicers.sku : 
                            selectedHierarchies.product === "Brand-Sub Brand" ? selectedSlicers.brandSubBrand : selectedSlicers.brand,
                    year: selectedSlicers.year
                },
                data: {
                    ...updateReqBody,
                    quarters: selectedQuarters,
                    periods: selectedPeriods,
                    hierarchy: selectedHierarchies.product,
                    timeline: selectedHierarchies.timeline
                }
            });

            toast.dismiss({
                id: updateToastID,
                containerId: TOAST_CONTAINER_ID,
            });

            if (updateAPIResponse.data.data) {
                updateGridDataAndSlicers(updateAPIResponse.data.data);
                setGridCellEdits({});

                toast.success("Volume Forecast data updated successfully", {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions("success"),
                });

                dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
                    [PROMOTION_PLANNING_TAB_ID]: false,
                    [SHORT_TERM_PROMOTION_SUB_TAB_ID]: false,
                    [FORECAST_REVIEW_TAB_ID]: false,
                    [ANNUAL_SUMMARY_TAB_ID]: false,
                    [TRADE_SPEND_REPORT_TAB_ID]: false
                }));

                setChangeLogDataFetched(true);
            } else if (updateAPIResponse.data.error) {
                toast.error(updateAPIResponse.data.error, {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions("error"),
                });
            }
        } catch (error: any) {
            console.error(error);
            toast.error("Error in updating the Volume Forecast data", {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions("error"),
            });
        }
    }, [globalCustomer.id, selectedSlicers, finalConsumptionForecast, selectedPeriods, selectedQuarters, dispatchFn, updateGridDataAndSlicers, fetchData]);

    /**
       * Re-calculate cell values by using Volume Forecast re-calculate API
       * @param {any} gridChanges Grid cell changes
       */
    const reCalculateCellValues = useCallback(async (gridChanges: any): Promise<void> => {
        try {
            const reCalculateToastID = toast.loading("Re-calculating Volume Forecast data...", {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions("loading"),
            });

            gridChanges['quarters'] = selectedQuarters;
            gridChanges['periods'] = selectedPeriods;

            const hasTotalKey = Object.values(gridChanges).some(value =>
                value && typeof value === 'object' && value.hasOwnProperty('total')
            );

            if (hasTotalKey) {
                let keysToRemove: any = ['customer_id', 'customer_name', 'sku_id', 'sku_name',
                     'variable', 'brand', 'sub_brand', 'rowIndex'];

                gridChanges['final_consumption_forecast'] = Object.keys(finalConsumptionForecast)
                    .filter(key => !keysToRemove.includes(key))
                    .reduce((acc: any, key: any) => {
                        acc[key] = finalConsumptionForecast[key];
                        return acc;
                    }, {});
            }

            const reCalculateAPIResponse: AxiosResponse<TradeAndPromotionPlanningFetchAPI<VolumeForecast[]>> = await fetchData("/volume-forecast/re-calculate", {
                method: "POST",
                params: {
                    customer_id: globalCustomer.id,
                    sku_id: selectedHierarchies.product === "Brand-Sub Brand-Volume Size" ? selectedSlicers.sku : 
                            selectedHierarchies.product === "Brand-Sub Brand" ? selectedSlicers.brandSubBrand : selectedSlicers.brand,
                    year: selectedSlicers.year
                },
                data: {
                    ...gridChanges,
                    hierarchy: selectedHierarchies.product,
                    timeline: selectedHierarchies.timeline
                }
            });

            toast.dismiss({
                id: reCalculateToastID,
                containerId: TOAST_CONTAINER_ID,
            });

            if (reCalculateAPIResponse.data.data) {
                const apiData: VolumeForecast[] = reCalculateAPIResponse.data.data;
                const reOrderedApiData: VolumeForecast[] = reOrderGridRows(apiData);

                setGridRowDataCopy(structuredClone(reOrderedApiData));
                setGridColDef((prevColDef) => generateColDef(reOrderedApiData, promotionDropdownData));

                toast.success("Volume Forecast data re-calculated successfully", {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions("success"),
                });

            } else if (reCalculateAPIResponse.data.error) {
                toast.error(reCalculateAPIResponse.data.error, {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions("error"),
                });
            }
        } catch (error: any) {
            console.error(error);
            toast.error("Error in re-calculating the Volume Forecast data", {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions("error"),
            });
        }
    }, [globalCustomer.id, promotionDropdownData, selectedSlicers, finalConsumptionForecast, selectedPeriods, selectedQuarters, fetchData, reOrderGridRows]);

    // }, [globalCustomer.id, promotionDropdownData, selectedSlicers, statForecast,
    //      selectedPeriods, selectedQuarters, fetchData, generateColDef, reOrderGridRows]);

    /**
     * Handler to reset cell edits.
     */
    const resetCellEditsHandler = useCallback((): void => {
        try {
            const resetConsent: boolean = window.confirm("Do you want to reset the changes done to the grid?");

            if (resetConsent) {
                setGridCellEdits({});
                setGridRowDataCopy(structuredClone(gridRowData));
                removeEditedCellHighlights();
            }

        } catch (error: Error | any) {
            console.error(`Error: ${error}`);
        }
    }, [gridRowData, removeEditedCellHighlights]);

    /**
   * Event handler for save button click
   * @param {any} event Save button click event data
   * @param {string} type Type: "update" or "reCalculate"
   */
    const saveCellEditsHandler = useCallback((event: any, type: "update" | "reCalculate", data:any={}): any => {
        try {
            /**
             * Returns Promotion ID for a Promotion Name.
             * @param {string} promotionName Promotion Name.
             * @returns Promotion ID.
             */
            const getPromoIdFromPromoName = (promotionName: string): number => {
                return promotionDropdownData.find(p => p.promotion_name === promotionName)?.promotion_id as number;
            }

            /**
             * Returns Promotion Calendar ID for a date string from grid data.
             * @param {string} usaDateStr Date string in the USA format.
             * @returns {number} Promotion Calendar ID.
             */
            const getPromoCalIdFromDate = (usaDateStr: string): number => {
                const promoCalRowIdx: number = rowLabelsAndIndexes["promotion_calendar_id"].rowIndex;
                const apiPromoCalIdRow: VolumeForecast = gridRowData[promoCalRowIdx];
                return apiPromoCalIdRow[usaDateStr] as number;
            };

            const updateReqBody: any = {};

            Object.keys(gridCellEdits).forEach((updateField: string) => {
                if (updateField === "override") {
                    updateReqBody["overrides"] = gridCellEdits[updateField];
                } else if (updateField === "promotion") {
                    const promoRowIdx: number = rowLabelsAndIndexes["promotion"].rowIndex;
                    const apiPromoIDMap: {
                        [usaDateStr: string]: string | null;
                    } = {};
                    const apiPromoNameRow: VolumeForecast = gridRowData[promoRowIdx];
                    const usaDateRegExp: RegExp = /^\d{2}\/\d{2}\/\d{4}$/;

                    // Generate promotion ID map for existing grid data
                    Object.entries(apiPromoNameRow).filter(([k, v]) => usaDateRegExp.test(k)).forEach(([usaDateStr, value]) => {
                        const promoName: string = value as string;
                        const isEverydayPromo: boolean = promoName.includes("ED");

                        apiPromoIDMap[usaDateStr] = (
                            isEverydayPromo ? null : `${getPromoIdFromPromoName(promoName)}-${getPromoCalIdFromDate(usaDateStr)}`
                        );
                    });

                    // Map updates to the existing promotion ID map
                    Object.keys(gridCellEdits[updateField]).forEach((usaDateStr) => {
                        const oldPromoName: string = apiPromoNameRow[usaDateStr] as string;
                        const newPromoName: string = gridCellEdits[updateField][usaDateStr] as string;

                        if (oldPromoName !== newPromoName) {
                            apiPromoIDMap[usaDateStr] = (
                                (newPromoName.includes("ED")) ? null : `${getPromoIdFromPromoName(newPromoName)}-new`
                            )
                        }
                    });

                    updateReqBody["promotions"] = apiPromoIDMap;
                } else {
                    updateReqBody[updateField] = gridCellEdits[updateField];
                }
            });

            if (Object.keys(updateReqBody).length) {
                if (type === "update") {
                    updateCellValues({...updateReqBody, ...data});
                } else {
                    reCalculateCellValues(updateReqBody);
                }
                return true;
            }else{
                return false;
            }

        } catch (error: Error | any) {
            console.error(`Error: ${error}`);
        }
    }, [gridCellEdits, promotionDropdownData, gridRowData, reCalculateCellValues, updateCellValues]);

    /**
     * Handler to update lag value using lag value update API.
     * @param {React.FormEvent} event Form submit event.
     * @returns {Promise<void>}
     */
    const updateLagValueHandler = async (event: React.FormEvent): Promise<void> => {
        event.preventDefault();

        if (newLagValue < -5 || newLagValue > 0) {
            toast.error("Kindly enter Lag value between -5 and 0", {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions('error')
            });
            return;
        }

        const updateLagValueToastID = toast.loading("Updating Lag value...", {
            containerId: TOAST_CONTAINER_ID,
            ...getToastOptions('loading')
        });

        try {
            const updateAPIResponse: AxiosResponse<TradeAndPromotionPlanningFetchAPI<{
                result: {
                    data: VolumeForecast[];
                    dropdown_data: {
                        promotions: PromotionDropdown[];
                    }
                },
                slicers: VolumeForecastDropdownModel
            }>> = await fetchData(`/volume-forecast`, {
                method: "PUT",
                params: {
                    customer_id: globalCustomer.id,
                    sku_id: selectedSlicers.sku,
                    year: selectedSlicers.year
                },
                data: {
                    quarters: selectedQuarters,
                    periods: selectedPeriods,
                    lag_value: newLagValue
                }
            });

            toast.dismiss({
                id: updateLagValueToastID,
                containerId: TOAST_CONTAINER_ID
            });

            if (updateAPIResponse.data.data) {
                resetNewLagValue();
                modalCloseBtnRef.current?.click();
                updateGridDataAndSlicers(updateAPIResponse.data.data);

                dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
                    [PROMOTION_PLANNING_TAB_ID]: false
                }));

                toast.success("Lag value updated successfully", {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions("success")
                });

            } else {
                toast.error('Error in updating the Lag value', {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions('error')
                });
            }

        } catch (error: Error | any) {
            console.error(`Request Error: ${error}`);
            toast.dismiss({
                id: updateLagValueToastID,
                containerId: TOAST_CONTAINER_ID
            });
            toast.error((
                error?.response?.data?.error ||
                'Error in updating the Lag value'
            ), {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions('error')
            });
        }
    };

    /**
     * Async function to fetch data from GET /change-log
     */
    const fetchChangeLogData = async (): Promise<void> => {
        const fetchingDataToastID = toast.loading('Fetching change log data...', {
            containerId: TOAST_CONTAINER_ID,
            ...getToastOptions('loading')
        });

        try {
            const fetchAPIResponse: AxiosResponse<TradeAndPromotionPlanningFetchAPI<any>> = await fetchData("/get-change-log", {
                method: "GET",
                params: {
                    tabName: activeSubTabId
                    }
            });

            if (fetchAPIResponse.data.data) {
                setChangeLogData(fetchAPIResponse.data.data.result);
                setChangeLogDataFetched(false);

            } else {
                toast.error('Error in fetching Change Log data', {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions('error')
                });
            }
            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });

        } catch (error: Error | any) {
            console.error("Error ", error)
            console.error(`Request Error: ${error}`);
            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });
            toast.error((
                error?.response?.data?.error ||
                'Error in fetching Change Log data'
            ), {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions('error')
            });
        }
    };


    // Lag Edit Modal body
    const lagUpdateModalBody: JSX.Element = (
        <form onSubmit={updateLagValueHandler}>
            <div className="row">
                <div className="col col-6 mb-2">
                    <label htmlFor="lag-value" className="form-label">New Lag value:</label>
                    <input
                        type="number"
                        min={-5}
                        max={0}
                        name="lag-value"
                        id="lag-value"
                        className="form-control"
                        value={newLagValue}
                        onChange={newLagValueChangeHandler}
                        onBlur={newLagValueBlurHandler}
                    />
                    {newLagValueHasError && (
                        <p className="text-danger">Lag value should be between -5 and 0</p>
                    )}
                </div>

                <div className="col col-6 mb-2">
                    <div className="col col-12 mb-2 d-grid">
                        <button disabled={!newLagValueIsValid} type="submit" className="btn btn-success">
                            <span className="fs-5 text-white">
                                <IoCheckmarkCircleOutline />
                            </span> Update
                        </button>
                    </div>

                    <div className="col col-12 mb-2 d-grid">
                        <button disabled={!newLagValueIsValid} type="button" className="btn btn-warning" onClick={(e) => closeLagEditModal()}>
                            <span className="fs-5">
                                <RxCrossCircled />
                            </span> Cancel
                        </button>
                    </div>
                </div>
            </div>
        </form>
    );

    useEffect(() => {
        if (
            !tabApiDataFetched &&
            activeSubTabId === VOLUME_FORECAST_TAB_ID
        ) {
            fetchVolumeForecastData();
        }
    }, [selectedSlicers, tabApiDataFetched, activeSubTabId, fetchVolumeForecastData]);


    return (
        <>  
            <div className="row mb-2">
                <div className="col col-11 text-start">
                    <HierarchySlicers/>
                </div>
            </div>
            <div className="row">
                <div className="col col-10 text-start">
                    <VolumeForecastSlicers isGridChanged={Object.keys(gridCellEdits).length > 0}/>
                </div>

                <div className="col col-2 text-end">
                    <div className="d-flex justify-content-end">
                        <div className="d-inline mx-1">
                        <button className="btn btn-warning btn-sm" data-bs-toggle="modal" 
                                    data-bs-target={"#changeLogModalVolumeForecastId"} onClick={()=>{
                                        if (changeLogDataFetched){
                                            fetchChangeLogData();
                                        }
                                    }}>
                                    Change Log
                        </button>
                        </div>

                    <EditableGridActions
                        tabId={VOLUME_FORECAST_TAB_ID}
                        disableIf={{
                            resetBtn: (Object.keys(gridCellEdits).length === 0),
                            reCalculateBtn: (Object.keys(gridCellEdits).length === 0),
                            saveBtn: (Object.keys(gridCellEdits).length === 0),
                        }}
                        onClick={{
                            reCalculateBtn: (e) => saveCellEditsHandler(e, "reCalculate"),
                            resetBtn: resetCellEditsHandler,
                            saveBtn: (e:MouseEventHandler<HTMLButtonElement>, data:any) => saveCellEditsHandler(e, "update", data)
                        }}
                        hideButtons={{
                            reCalculateBtn: false,
                            addRowBtn: true,
                            deleteRowBtn: true
                        }}
                    />
                    </div>
                </div>
            </div>

            <div className="ag-theme-balham mt-2" style={{ height: props.gridHeight, maxHeight: props.gridHeight, width: '100%', overflowX: 'auto', overflowY: 'auto' }}>
                <AgGridReact
                    ref={agGridRef}
                    rowData={gridRowDataCopy}
                    columnDefs={gridColDef}
                    singleClickEdit={true}
                    gridOptions={{
                        ...(AG_GRID_OPTIONS as any),
                        suppressMenuHide: true,
                        isExternalFilterPresent: (params: any) => true,
                        getRowStyle: gridRowStyleHandler,
                        onCellValueChanged: cellValueChangeHandler,
                        doesExternalFilterPass: (node: any): boolean => {
                            if (node.data) {
                                return node.data?.variable !== "promotion_calendar_id" && node.data?.variable !== "promotion_type";
                            }
                            return true;
                        }
                    }}
                    defaultColDef={{
                        ...AG_GRID_DEFAULT_COL_DEF,
                    }}
                    modules={[
                        ...AG_GRID_MODULES,
                        ColumnsToolPanelModule,
                        FiltersToolPanelModule,
                        SetFilterModule
                    ]}
                />
            </div>

            {
                createPortal(
                    <Modal
                        id={lagUpdateModalID}
                        ref={modalCloseBtnRef}
                        title={"Update Lag Value"}
                        onModalClose={() => { }}
                    >
                        {lagUpdateModalBody}
                    </Modal>,
                    document.getElementById("modal-root") as HTMLElement
                )
            }

            {
                createPortal(
                    <Modal
                        id={"changeLogModalVolumeForecastId"}
                        ref={modalCloseBtnRef}
                        title={""}
                        onModalClose={() => { }}
                        width="xl"
                    >
                        <ChangeLogTable changeLogData={changeLogData}/>
                    </Modal>,
                    document.getElementById("modal-root") as HTMLElement
                )
            }
        </>
    );
};

export default VolumeForecastGrid;
